import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  effect,
  inject,
  viewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TranslateService } from '@ngx-translate/core';
import cloneDeep from 'lodash/cloneDeep';
import mapboxgl, { LngLatLike } from 'mapbox-gl';

import { BulkDatabaseVersion } from 'src/app/core/api/models';
import { MapsPostalCodesService } from 'src/app/core/api/services';
import { NumberSeparatorPipe } from 'src/app/core/pipes/number.separator.pipe';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { AppConfigService } from 'src/app/core/services/config.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import { AppSim } from 'src/app/core/types/app-sim.type';
import { ZonesSelectSearchComponent } from '../select-search/zones-select-search.component';
import { map_color_map } from '../smart-send-map-control/smart-send-map-control.component';

/**
 * this component use map.ir for showing the postal code's
 */
@Component({
    selector: 'postal-code-map-selector',
    templateUrl: './postal-code-map-selector.html',
    styleUrls: ['./postal-code-map-selector.scss'],
    standalone: false
})
export class PostalCodeMapSelectorComponent implements OnInit {
  appStateService = inject(AppStateService);
  isDisabledHamrahAvalAddBtn = false;
  isDisabledIrancellAddBtn = false;
  stateId: number = -1;
  cityId: number = -1;
  center: LngLatLike = [51.680374, 32.661343];
  map: mapboxgl.Map = null;
  cursorStyle: string = '';
  @Input('Theme') Theme: string = map_color_map.light;
  @Input('Zoom') Zoom: number = 6;
  cityForm: FormGroup;
  loading: boolean = true;
  hoverFilter = ['==', 'postalCode', ''];
  selectedFilter: any[] = ['==', 'postalCode', ''];
  InQueryFilter: any[] = ['==', 'postalCode', ''];
  //#region selected layer properties
  selectedLayer: postalCodeArea = null;
  selectedLayerTotalHamrahAval: number = 0;
  selectedLayerTotalIrancell: number = 0;
  hamrahAvalSendingInfo: SendSmsInfo = {
    startFrom: 1,
    sendCount: 0,
    totalCount: 0,
    description: '',
  };
  irancellSendingInfo: SendSmsInfo = {
    sendCount: 0,
    totalCount: 0,
    startFrom: 1,
    description: '',
  };
  EditableItem: MapPostalCodeSendSmsInfo = null;
  Items: MapPostalCodeSendSmsInfo[] = [];
  finalItems: MapPostalCodeSendSmsInfo[] = [];
  descriptionField: string = '';
  @Output('onChange') ItemsChanged: EventEmitter<MapPostalCodeSendSmsInfo[]> =
    new EventEmitter<MapPostalCodeSendSmsInfo[]>();
  @Output('userConfirmed') userConfirmed: EventEmitter<
    MapPostalCodeSendSmsInfo[]
  > = new EventEmitter<MapPostalCodeSendSmsInfo[]>();
  @Output('onTotalCountChanged')
  totalCountChanged: EventEmitter<MapTotalSelectedNumbers> =
    new EventEmitter<MapTotalSelectedNumbers>();
  //#endregion
  polygons: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    features: [],
    type: 'FeatureCollection',
  };
  @Input('ColoringSettings')
  ColoringSettings: PostalCodeMapSelectorColoringSettings = {
    Color: '#accdec',
    Opacity: 0.5,
    HoverColor: '#8ca2d4',
    HoverOpacity: 0.8,
    SelectedColor: '#8e74b3',
    SelectedOpacity: 0.8,
    InQueryColor: '#01b46d',
    InQueryOpacity: 0.5,
  };
  @Input('bulkDatabaseVersion') bulkDatabaseVersion: BulkDatabaseVersion;
  readonly sidePanel = viewChild<ElementRef<HTMLDivElement>>('sidePanel');
  readonly selCities = viewChild<ZonesSelectSearchComponent>('selCities');
  readonly operators = viewChild<MatExpansionPanel>('operators');
  readonly selStates = viewChild<ZonesSelectSearchComponent>('selStates');

  containerOpened: boolean = false;
  isWrongHamrahAvalIndex: boolean = false;
  isWrongIrancellIndex: boolean = false;
  mciImage: string;
  rightelImage: string;
  styleUrl: string;
  transformReq: any;

  constructor(
    public mapPostalCodeService: MapsPostalCodesService,
    private CD: ChangeDetectorRef,
    protected translate: TranslateService,
    protected numberSeparatorPipe: NumberSeparatorPipe
  ) {
    effect(() => {
      this.mciImage =
        this.appStateService.currentThemeMode() == 'dark'
          ? '/assets/media/svg/operator/mci-dark.svg'
          : '/assets/media/svg/operator/mci.svg';
      this.rightelImage =
        this.appStateService.currentThemeMode() == 'dark'
          ? '/assets/media/svg/operator/rightel-dark.svg'
          : '/assets/media/svg/operator/rightel.svg';
    });
  }

  ngOnInit(): void {
    this.cityForm = new FormGroup({
      stateId: new FormControl('', Validators.required),
      cityId: new FormControl('', Validators.required),
    });
    this.getMapStyleUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.Theme) this.getMapStyleUrl();
  }

  public toggleContainer() {
    this.containerOpened = !this.containerOpened;
    if (this.containerOpened)
      this.sidePanel().nativeElement.classList.add('open');
    else this.sidePanel().nativeElement.classList.remove('open');
    this.fitBound();
    this.map.resize();
    // this.console.end();
  }

  getMapStyleUrl() {
    let lightThemeUrl;
    let darkThemeUrl;
    lightThemeUrl = AppConfigService.config.useMapIr
      ? AppConfigService.config.mapIrStyleUrl
      : AppConfigService.config.gisStyleUrl;
    darkThemeUrl = AppConfigService.config.useMapIr
      ? AppConfigService.config.mapIrStyleDarkUrl
      : AppConfigService.config.gisStyleDarkUrl;
    if (this.Theme == map_color_map.light) {
      this.styleUrl = lightThemeUrl;
    } else if (this.Theme == map_color_map.dark) this.styleUrl = darkThemeUrl;
    ConsoleService.info('this.styleUrl', this.styleUrl);
  }

  transformRequest(url: string, resourceType: any) {
    if (AppConfigService.config.useMapIr)
      // use for map.ir
      this.transformReq = {
        url: url,
        headers: {
          'x-api-key': AppConfigService.config.mapIrXApiKey,
        },
      };
    // use for gis
    else
      this.transformReq = {
        url: url,
        headers: {
          Accept:
            'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
          Origin: 'http://localhost:4200',
          'Access-Control-Request-Headers': 'access-control-allow-origin',
          'Access-Control-Request-Method': 'GET',
        },
      };
  }

  doItemChanges() {
    this.makeInQueryFilter();
    this.ItemsChanged.emit(this.Items);
  }

  //#region map event's
  onMapReady($event) {
    this.map = $event;

    this.loading = false;
    this.toggleContainer();
  }

  changeSelectedLayer(newSelectedLayer: postalCodeArea) {
    this.isDisabledHamrahAvalAddBtn = false;
    this.isDisabledIrancellAddBtn = false;
    this.selectedLayer = newSelectedLayer;
    if (this.containerOpened == false) this.toggleContainer();
    this.selectedLayerTotalHamrahAval =
      this.selectedLayer.hamrahAvalNumberCount;
    this.selectedLayerTotalIrancell = this.selectedLayer.irancellNumberCount;
    // if (this.irancellSendingInfo.sendCount == 0)
    //   this.irancellSendingInfo.sendCount = this.selectedLayerTotalIrancell;

    this.EditableItem = null;
    //now most find if selected layer is in result set or not
    for (let index = 0; index < this.Items.length; index++) {
      let item = this.Items[index];
      if (
        item.cityId == this.selectedLayer.cityId &&
        item.stateId == this.selectedLayer.stateId &&
        item.postalCode == this.selectedLayer.postalCode
      ) {
        this.EditableItem = item;
        this.irancellSendingInfo = item.irancellInfo;
        this.hamrahAvalSendingInfo = item.hamrahAvalInfo;

        if (this.irancellSendingInfo == null) {
          this.irancellSendingInfo = {
            startFrom: 1,
            sendCount: this.selectedLayerTotalIrancell,
            totalCount: this.selectedLayerTotalIrancell,
            description: '',
          };
        }

        if (this.hamrahAvalSendingInfo == null) {
          this.hamrahAvalSendingInfo = {
            startFrom: 1,
            sendCount: this.selectedLayerTotalHamrahAval,
            totalCount: this.selectedLayerTotalHamrahAval,
            description: '',
          };
        }
        break;
      }
    }
    if (this.EditableItem == null) {
      //now found area
      this.irancellSendingInfo = {
        startFrom: 1,
        sendCount: this.selectedLayerTotalIrancell,
        totalCount: this.selectedLayerTotalIrancell,
        description: '',
      };
      this.hamrahAvalSendingInfo = {
        startFrom: 1,
        sendCount: this.selectedLayerTotalHamrahAval,
        totalCount: this.selectedLayerTotalHamrahAval,
        description: '',
      };
    }
    this.CD.detectChanges();
    const operators = this.operators();
    if (operators) operators.open();
  }
  onClick(evt: any) {
    this.isWrongIrancellIndex = false;
    this.isWrongHamrahAvalIndex = false;
    let obj = evt.features?.[0] as GeoJSON.Feature<GeoJSON.Geometry>;
    this.selectedFilter = ['==', 'postalCode', obj.properties?.['postalCode']];

    this.changeSelectedLayer(obj.properties as postalCodeArea);
    //alert(obj.properties?.["postalCode"]);
  }

  activateHoverOn(evt: any) {
    this.hoverFilter = [
      '==',
      'postalCode',
      evt.features[0].properties.postalCode,
    ];
    this.cursorStyle = 'pointer';
  }

  disableHover() {
    this.hoverFilter = ['==', 'postalCode', ''];
    this.cursorStyle = '';
  }
  //#endregion
  private pPostalCodes: postalCodeArea[];
  /**
   * this method load the polygon's into the map
   * @param postalCodes
   */
  public setPostalCodes(postalCodes: postalCodeArea[]) {
    this.clearPostalCodes();
    this.pPostalCodes = postalCodes;
    postalCodes.forEach((element) => {
      this.polygons.features.push({
        geometry: {
          type: 'Polygon',
          coordinates: [element.coordinates],
        },
        properties: element,
        type: 'Feature',
      });
    });
    (this.map.getSource('oneline') as mapboxgl.GeoJSONSource).setData(
      this.polygons
    );
    this.fitBound();
    // const bounds = new mapboxgl.LngLatBounds([
    //     postalCodes[0].coordinates[0][0],
    //     postalCodes[0].coordinates[0][1],
    //     postalCodes[0].coordinates[1][0],
    //     postalCodes[0].coordinates[1][1]
    //     ]
    //     );
    //     for(const pcode of postalCodes)
    //     // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
    //     for (const coord of pcode.coordinates) {
    //         bounds.extend([coord[0],coord[1]]);
    //     }

    //     this.map.fitBounds(bounds, {
    //     padding: 20
    //     });
  }
  private fitBound() {
    if (!this.pPostalCodes) return;
    const bounds = new mapboxgl.LngLatBounds([
      this.pPostalCodes[0].coordinates[0][0],
      this.pPostalCodes[0].coordinates[0][1],
      this.pPostalCodes[0].coordinates[1][0],
      this.pPostalCodes[0].coordinates[1][1],
    ]);
    // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
    for (const pcode of this.pPostalCodes)
      for (const coord of pcode.coordinates) {
        bounds.extend([coord[0], coord[1]]);
      }

    this.map.fitBounds(bounds, {
      padding: 20,
    });
  }
  /**
   * this method reset polygon's on the map
   */
  public clearPostalCodes() {
    this.polygons.features = [];
    (this.map.getSource('oneline') as mapboxgl.GeoJSONSource).setData(
      this.polygons
    );
  }

  //#region side area function's
  public setStateId(event) {
    this.stateId = event;
    this.setCityId(-1);
  }
  public setCityId(event) {
    if (this.cityId == event) return;
    //this.loading=true;
    this.cityId = event;
    //TODO: here we most load city postalCode's
    this.clearPostalCodes();
    this.mapPostalCodeService
      .mapsPostalCodesGetMapsPostalCodesByStateAndCityId({
        StateId: this.stateId,
        CityId: this.cityId,
        BulkDatabaseVersion: this.bulkDatabaseVersion,
      })
      .subscribe((result) => {
        if (result.data?.items && result.data?.items?.length > 0) {
          //now most show polygon's
          let a: postalCodeArea[] = [];
          result.data?.items.forEach((item) => {
            let newCoordinates: GeoJSON.Position[];
            if (item.position.indexOf(';') > 0) {
              let coordinates = JSON.parse(
                '[[' + item.position.replaceAll(';', '],[') + ']]'
              );
              newCoordinates = [];
              coordinates.forEach((element: number[]) => {
                newCoordinates.push([element[1], element[0]]);
              });
            } else {
              newCoordinates = JSON.parse(item.position);
            }
            a.push({
              cityId: this.cityId,
              coordinates: newCoordinates,
              hamrahAvalNumberCount: item.hamrahavalCount,
              irancellNumberCount: item.irancellCount,
              postalCode: item.postalCode.toString(),
              stateId: this.stateId,
            });
          });
          this.setPostalCodes(a);
          //this.loading=false;
        }
      });
  }

  addOrUpdateHamrahAval(area: postalCodeArea) {
    let cityName = '';
    this.selCities().items.forEach((item) => {
      if (item.id == this.cityId) cityName = item.name;
    });

    this.hamrahAvalSendingInfo.description +=
      this.translate.instant('pages.sendBalkSms.fields.totalMobileNumber') +
      ' ' +
      this.numberSeparatorPipe.transform(this.selectedLayerTotalHamrahAval) +
      ' ';

    if (this.EditableItem == null) {
      //we just need to create new item and push to the item's

      this.EditableItem = {
        cityId: area.cityId,
        stateId: area.stateId,
        hamrahAvalInfo: this.hamrahAvalSendingInfo,
        irancellInfo: {
          sendCount: 0,
          startFrom: 1,
          totalCount: this.selectedLayerTotalIrancell,
          description: '',
        },
        postalCode: area.postalCode,
        cityName: cityName,
        coordinates: area.coordinates,
      };
      this.Items.push(this.EditableItem);
      this.setTotalSendCount();
      this.doItemChanges();
    } else {
      //first most search in the loop and most change the item
      for (let index = 0; index < this.Items.length; index++) {
        let item = this.Items[index];
        if (
          item.stateId == area.stateId &&
          item.cityId == area.cityId &&
          item.postalCode == area.postalCode
        ) {
          item.hamrahAvalInfo = this.hamrahAvalSendingInfo;
          this.EditableItem = item;
          this.Items[index] = item;
          this.setTotalSendCount();
          this.doItemChanges();
          break;
        }
      }
    }
  }

  addOrUpdateIrancell(area: postalCodeArea) {
    let cityName = '';
    this.selCities().items.forEach((item) => {
      if (item.id == this.cityId) cityName = item.name;
    });
    this.irancellSendingInfo.description +=
      this.translate.instant('pages.sendBalkSms.fields.totalMobileNumber') +
      ' ' +
      this.numberSeparatorPipe.transform(this.selectedLayerTotalIrancell) +
      ' ';

    if (this.EditableItem == null) {
      //we just need to create new item and push to the item's
      this.EditableItem = {
        cityId: area.cityId,
        stateId: area.stateId,
        hamrahAvalInfo: {
          sendCount: 0,
          startFrom: 1,
          totalCount: this.selectedLayerTotalHamrahAval,
          description: '',
        },
        irancellInfo: this.irancellSendingInfo,
        postalCode: area.postalCode,
        cityName: cityName,
        coordinates: area.coordinates,
      };
      this.Items.push(this.EditableItem);
      this.setTotalSendCount();
      this.doItemChanges();
    } else {
      //first most search in the loop and most change the item
      for (let index = 0; index < this.Items.length; index++) {
        let item = this.Items[index];
        if (
          item.stateId == area.stateId &&
          item.cityId == area.cityId &&
          item.postalCode == area.postalCode
        ) {
          item.irancellInfo = this.irancellSendingInfo;
          this.EditableItem = item;
          this.Items[index] = item;
          this.setTotalSendCount();
          this.doItemChanges();
          break;
        }
      }
    }
  }

  removeHamrahAval(area: postalCodeArea) {
    if (this.EditableItem != null && this.EditableItem.hamrahAvalInfo != null) {
      for (let index = 0; index < this.Items.length; index++) {
        let item = this.Items[index];
        if (
          item.cityId == area.cityId &&
          item.stateId == area.stateId &&
          item.postalCode == area.postalCode
        ) {
          if (item.irancellInfo == null || item.irancellInfo.sendCount == 0) {
            this.Items.splice(index, 1);
            this.EditableItem = null;
            this.setTotalSendCount();
            this.doItemChanges();
            return;
          } else {
            this.Items[index].hamrahAvalInfo = {
              sendCount: 0,
              startFrom: 1,
              totalCount: this.selectedLayerTotalHamrahAval,
              description: '',
            };
            this.setTotalSendCount();
            this.doItemChanges();
          }
        }
      }
    }
  }

  removeIrancell(area: postalCodeArea) {
    if (this.EditableItem != null && this.EditableItem.irancellInfo != null) {
      for (let index = 0; index < this.Items.length; index++) {
        let item = this.Items[index];
        if (
          item.cityId == area.cityId &&
          item.stateId == area.stateId &&
          item.postalCode == area.postalCode
        ) {
          if (
            item.hamrahAvalInfo == null ||
            item.hamrahAvalInfo.sendCount == 0
          ) {
            this.Items.splice(index, 1);
            this.EditableItem = null;
            this.setTotalSendCount();
            this.doItemChanges();
            return;
          } else {
            this.Items[index].irancellInfo = {
              sendCount: 0,
              startFrom: 1,
              totalCount: this.selectedLayerTotalIrancell,
              description: '',
            };
            this.setTotalSendCount();
            this.doItemChanges();
          }
        }
      }
    }
  }

  makeInQueryFilter() {
    if (this.Items.length == 0) {
      this.InQueryFilter = ['==', 'postalCode', ''];
    } else {
      let query: any[] = ['any'];
      for (let index = 0; index < this.Items.length; index++) {
        query.push(['==', 'postalCode', this.Items[index].postalCode]);
      }
      this.InQueryFilter = query;
    }
  }

  moveToSendingInfo(item: MapPostalCodeSendSmsInfo) {
    this.selectedLayer.cityId = item.cityId;
    this.selectedLayer.stateId = item.stateId;
    this.selectedLayer.postalCode = item.postalCode;
    this.selectedLayer.hamrahAvalNumberCount = item.hamrahAvalInfo?.totalCount;
    this.selectedLayer.irancellNumberCount = item.irancellInfo?.totalCount;
    this.selectedLayer.coordinates = item.coordinates;

    this.changeSelectedLayer(this.selectedLayer);

    this.operators().open();
  }

  checkTotalCountIsValid(simType: AppSim) {
    switch (simType) {
      case 'hamrahAval':
        if (
          this.hamrahAvalSendingInfo.startFrom +
            this.hamrahAvalSendingInfo.sendCount >
          this.selectedLayerTotalHamrahAval + 1
        ) {
          this.isDisabledHamrahAvalAddBtn = true;
          this.isWrongHamrahAvalIndex = true;
        } else {
          this.isWrongHamrahAvalIndex = false;
          this.isDisabledHamrahAvalAddBtn = false;
        }
        break;
      case 'irancell':
        if (
          this.irancellSendingInfo.startFrom +
            this.irancellSendingInfo.sendCount >
          this.selectedLayerTotalIrancell + 1
        ) {
          this.isDisabledIrancellAddBtn = true;
          this.isWrongIrancellIndex = true;
        } else {
          this.isWrongIrancellIndex = false;
          this.isDisabledIrancellAddBtn = false;
        }
        break;
    }
  }

  sendFromIndexChanged(fromIndex, simType: AppSim) {
    fromIndex = this.getNumber(fromIndex);
    switch (simType) {
      case 'hamrahAval':
        this.hamrahAvalSendingInfo.startFrom = fromIndex;
        break;
      case 'irancell':
        this.irancellSendingInfo.startFrom = fromIndex;
        break;
    }
    this.checkTotalCountIsValid(simType);
  }

  sendCountChanged(count, simType: AppSim) {
    count = this.getNumber(count);
    switch (simType) {
      case 'hamrahAval':
        {
          if (count == 0) this.isDisabledHamrahAvalAddBtn = true;
          else if (count > 0) {
            this.isDisabledHamrahAvalAddBtn = false;
            this.hamrahAvalSendingInfo.sendCount = count;
          }
        }
        break;
      case 'irancell':
        {
          if (count == 0) this.isDisabledIrancellAddBtn = true;
          else if (count > 0) {
            this.isDisabledIrancellAddBtn = false;
            this.irancellSendingInfo.sendCount = count;
          }
        }
        break;
    }
    this.checkTotalCountIsValid(simType);
  }

  getNumber(num) {
    if (typeof num == 'number') {
      return num;
    }

    let number: Number = +num?.toString()?.getJustNumber();
    if (number) return +number;
    else return 0;
  }

  totalSendCount: MapTotalSelectedNumbers = { irancell: 0, hamrahAval: 0 };
  setTotalSendCount() {
    let hamrahCount = 0;
    let irancellCount = 0;
    if (this.Items.length > 0)
      for (let index = 0; index < this.Items.length; index++) {
        if (this.Items[index].hamrahAvalInfo != null)
          hamrahCount += this.Items[index].hamrahAvalInfo.sendCount;
        if (this.Items[index].irancellInfo != null)
          irancellCount += this.Items[index].irancellInfo?.sendCount;
      }
    this.totalSendCount = {
      irancell: irancellCount,
      hamrahAval: hamrahCount,
    };
    this.totalCountChanged.emit(this.totalSendCount);
  }

  stepItemToText(items: MapPostalCodeSendSmsInfo[]) {
    try {
      let separator = ' ';
      ConsoleService.info('items', items);

      items.forEach((item: MapPostalCodeSendSmsInfo) => {
        if (item.hamrahAvalInfo?.sendCount > 0) {
          const selStates = this.selStates();
          if (item.stateId && selStates?.items) {
            for (let index = 0; index < selStates?.items.length; index++) {
              if (selStates?.items[index].id == item.stateId)
                item.hamrahAvalInfo.description +=
                  this.translate.instant('common.state') +
                  separator +
                  selStates?.items[index].name;
            }
            item.hamrahAvalInfo.description += separator;
          }
          if (item.cityName) {
            item.hamrahAvalInfo.description +=
              this.translate.instant('common.city') + separator + item.cityName;
            item.hamrahAvalInfo.description += separator;
          }
          if (item.postalCode && item.postalCode.length > 0) {
            item.hamrahAvalInfo.description +=
              this.translate.instant('common.postalCode') +
              separator +
              item.postalCode;
            item.hamrahAvalInfo.description += separator;
          }
          item.hamrahAvalInfo.description +=
            this.translate.instant('pages.sendBalkSms.fields.operatorType') +
            separator +
            this.translate.instant('common.mci');
          item.hamrahAvalInfo.description += separator;

          item.hamrahAvalInfo.description =
            item.hamrahAvalInfo.description.replaceAll('  ', ' ');
        }
        if (item.irancellInfo?.sendCount > 0) {
          const selStates = this.selStates();
          if (item.stateId && selStates?.items) {
            for (let index = 0; index < selStates?.items.length; index++) {
              if (selStates?.items[index].id == item.stateId)
                item.irancellInfo.description +=
                  this.translate.instant('common.state') +
                  separator +
                  selStates?.items[index].name;
            }
            item.irancellInfo.description += separator;
          }
          if (item.cityName) {
            item.irancellInfo.description +=
              this.translate.instant('common.city') + separator + item.cityName;
            item.irancellInfo.description += separator;
          }
          if (item.postalCode && item.postalCode.length > 0) {
            item.irancellInfo.description +=
              this.translate.instant('common.postalCode') +
              separator +
              item.postalCode;
            item.irancellInfo.description += separator;
          }
          item.irancellInfo.description +=
            this.translate.instant('pages.sendBalkSms.fields.operatorType') +
            separator +
            this.translate.instant('common.mtn');
          item.irancellInfo.description += separator;

          item.irancellInfo.description =
            item.irancellInfo.description.replaceAll('  ', ' ');
        }
      });
    } catch (error) {
      ConsoleService.error('error', error);
    }
  }

  confirmAndNextStep() {
    this.finalItems = cloneDeep(this.Items);

    this.finalItems.forEach((item) => {
      if (item.hamrahAvalInfo?.sendCount == 0) item.hamrahAvalInfo = null;
      if (item.irancellInfo?.sendCount == 0) item.irancellInfo = null;
    });
    this.stepItemToText(this.finalItems);
    this.userConfirmed.emit(this.finalItems);
  }
  //#endregion
}

export class postalCodeArea {
  public postalCode: string;
  public cityId: number;
  public stateId: number;
  public hamrahAvalNumberCount: number;
  public irancellNumberCount: number;
  public coordinates: GeoJSON.Position[];
}

export class PostalCodeMapSelectorColoringSettings {
  public Color: string;
  public Opacity: number;
  public HoverColor: string;
  public HoverOpacity: number;
  public SelectedColor: string;
  public SelectedOpacity: number;
  public InQueryColor: string;
  public InQueryOpacity: number;
}

export interface SendSmsInfo {
  startFrom: number;
  sendCount: number;
  totalCount: number;
  description: string;
}
export interface MapPostalCodeSendSmsInfo {
  stateId: number;
  cityId: number;
  postalCode: string;
  irancellInfo: SendSmsInfo;
  hamrahAvalInfo: SendSmsInfo;
  cityName: string;
  coordinates: GeoJSON.Position[];
}
export interface MapPstalCodeSendSmsInfo {
  items: MapPostalCodeSendSmsInfo[];
}
export interface MapTotalSelectedNumbers {
  irancell: number;
  hamrahAval: number;
}
